import { axiosSecuredInstance } from "configurations/axiosConfig";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";

type RawState = {
  Active: number;
  StateAbbr: string;
  StateCode: string;
  StateName: string;
};

// `value` is StateAbbr (kept for backward-compat with all existing consumers);
// `stateCode` is the numeric code used by lookups that filter by state.
export type StateOption = { label: string; value: string; stateCode: string };

// Module-level promise cache. The state list is static; one fetch per page load
// is plenty. Subsequent callers reuse the same promise — no thundering herd.
let cachedStatesPromise: Promise<StateOption[]> | null = null;

export const getStateList = (): Promise<StateOption[]> => {
  if (cachedStatesPromise) return cachedStatesPromise;
  cachedStatesPromise = axiosSecuredInstance
    .get<RawState[]>("/lookup/GetStateList/840")
    .then(({ data }) =>
      (Array.isArray(data) ? data : [])
        .filter((s) => s.Active === 1)
        .map((s) => ({
          label: s.StateName ? `${s.StateAbbr} - ${s.StateName}` : s.StateAbbr,
          value: s.StateAbbr,
          stateCode: s.StateCode,
        }))
    )
    .catch((error) => {
      console.error("getStateList failed", error);
      cachedStatesPromise = null;
      return [];
    });
  return cachedStatesPromise;
};

// PascalCase wire DTO from /api/Lookup/GetCountyList. Matches the
// Stewart.Shared County entity exactly (Startup.cs sets
// JsonSerializerOptions.PropertyNamingPolicy = null).
export type CountyDto = {
  CountyID: number;
  CountryCodeNum: string;
  StateAbbr: string;
  StateCode: string;
  StateName: string;
  CountyCode: string;
  CountyName: string;
  FIPS: string;
  SortOrder: number;
};

export type CountyOption = { label: string; value: string, countyCode: string; countyName: string };

// Cache by (countryCodeNum, stateCode). One Map entry per arg pair —
// caches different arg combinations independently if callers diverge later.
const cachedCountiesByKey = new Map<string, Promise<CountyOption[]>>();

export const getCountyList = (
  countryCodeNum: string,
  stateCode?: string,
  stateCodeList?: string
): Promise<CountyOption[]> => {
  const key = `${countryCodeNum}|${stateCode ?? ""}|${stateCodeList ?? ""}`;
  const cached = cachedCountiesByKey.get(key);
  if (cached) return cached;

  // axios drops `undefined` params, so unset args are omitted from the URL.
  const promise = axiosSecuredInstance
    .get<CountyDto[]>("/lookup/GetCountyList", {
      params: { countryCodeNum, stateCode, stateCodeList },
    })
    .then(({ data }) =>
      (Array.isArray(data) ? data : []).map((c) => ({
        label: `${c.CountyCode} - ${c.CountyName}, ${c.StateAbbr}`,
        value: `${c.StateAbbr}${c.CountyCode}`,
        countyCode: c.CountyCode,
        countyName: c.CountyName,
      }))
    )
    .catch((error) => {
      console.error("getCountyList failed", error);
      cachedCountiesByKey.delete(key);
      return [];
    });

  cachedCountiesByKey.set(key, promise);
  return promise;
};

// PascalCase wire DTO from /api/lookup/GetFormPrefixList. Matches the
// Stewart.Shared `FormPrefix` entity. Used by both the Policy Type and
// Form Prefix column-header dropdowns in Policy Management.
export type FormPrefixDto = {
  FormPrefixCode: number;
  FormPrefixDescription: string;
  FormType: string;
  ProductTypeAbbr: string;
  ProductType: string;
  IsInventory: string;
  IsActive: string;
  PolicyTypeDesc: string;
};

let cachedFormPrefixListPromise: Promise<FormPrefixDto[]> | null = null;

export const getFormPrefixList = (): Promise<FormPrefixDto[]> => {
  if (cachedFormPrefixListPromise) return cachedFormPrefixListPromise;
  cachedFormPrefixListPromise = axiosSecuredInstance
    .get<FormPrefixDto[]>("/lookup/GetFormPrefixList")
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error("getFormPrefixList failed", error);
      cachedFormPrefixListPromise = null;
      return [];
    });
  return cachedFormPrefixListPromise;
};

// PascalCase wire DTO from /api/lookup/GetPIList/PropertyType.
export type PropertyTypeDto = {
  PropertyTypeCode: string;
  PropertyTypeName: string;
};

let cachedPropertyTypeListPromise: Promise<PropertyTypeDto[]> | null = null;

export const getPropertyTypeList = (): Promise<PropertyTypeDto[]> => {
  if (cachedPropertyTypeListPromise) return cachedPropertyTypeListPromise;
  cachedPropertyTypeListPromise = axiosSecuredInstance
    .get<PropertyTypeDto[]>("/lookup/GetPIList/PropertyType")
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error("getPropertyTypeList failed", error);
      cachedPropertyTypeListPromise = null;
      return [];
    });
  return cachedPropertyTypeListPromise;
};

// PascalCase wire DTO from /api/lookup/GetPIList/PremiumReceivableType.
export type PremiumReceivableTypeDto = {
  PremiumReceivableTypeCode: string;
  PremiumReceivableTypeName: string;
};

let cachedPremiumReceivableTypeListPromise: Promise<
  PremiumReceivableTypeDto[]
> | null = null;

// No subType: BD and WO carry SubType = 'LEGACY' in dbo.PremiumReceivableType,
// and the SP filters on it whenever one is supplied — passing anything here
// would silently drop those two types.
export const getPremiumReceivableTypeList = (): Promise<
  PremiumReceivableTypeDto[]
> => {
  if (cachedPremiumReceivableTypeListPromise)
    return cachedPremiumReceivableTypeListPromise;
  cachedPremiumReceivableTypeListPromise = axiosSecuredInstance
    .get<PremiumReceivableTypeDto[]>("/lookup/GetPIList/PremiumReceivableType")
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error("getPremiumReceivableTypeList failed", error);
      cachedPremiumReceivableTypeListPromise = null;
      return [];
    });
  return cachedPremiumReceivableTypeListPromise;
};

// PascalCase wire DTO from /api/lookup/GetPIList/ReceiptType.
export type ReceiptTypeDto = {
  ReceiptTypeCode: string;
  ReceiptTypeName: string;
};

let cachedReceiptTypeListPromise: Promise<ReceiptTypeDto[]> | null = null;

export const getReceiptTypeList = (): Promise<ReceiptTypeDto[]> => {
  if (cachedReceiptTypeListPromise) return cachedReceiptTypeListPromise;
  cachedReceiptTypeListPromise = axiosSecuredInstance
    .get<ReceiptTypeDto[]>("/lookup/GetPIList/ReceiptType")
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error("getReceiptTypeList failed", error);
      cachedReceiptTypeListPromise = null;
      return [];
    });
  return cachedReceiptTypeListPromise;
};

// PascalCase wire DTO from /api/lookup/GetPIList/RecordType.
export type RecordTypeDto = {
  RecordTypeCode: string;
  RecordTypeName: string;
};

let cachedRecordTypeListPromise: Promise<RecordTypeDto[]> | null = null;

export const getRecordTypeList = (): Promise<RecordTypeDto[]> => {
  if (cachedRecordTypeListPromise) return cachedRecordTypeListPromise;
  cachedRecordTypeListPromise = axiosSecuredInstance
    .get<RecordTypeDto[]>("/lookup/GetPIList/RecordType")
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error("getRecordTypeList failed", error);
      cachedRecordTypeListPromise = null;
      return [];
    });
  return cachedRecordTypeListPromise;
};

// PascalCase wire DTO from /api/lookup/GetPIList/PolicyErrorType.
export type PolicyErrorTypeDto = {
  PolicyErrorTypeCode: string;
  PolicyErrorTypeName: string;
};

let cachedPolicyErrorTypeListPromise: Promise<PolicyErrorTypeDto[]> | null = null;

export const getPolicyErrorTypeList = (): Promise<PolicyErrorTypeDto[]> => {
  if (cachedPolicyErrorTypeListPromise) return cachedPolicyErrorTypeListPromise;
  cachedPolicyErrorTypeListPromise = axiosSecuredInstance
    .get<PolicyErrorTypeDto[]>("/lookup/GetPIList/PolicyErrorType")
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error("getPolicyErrorTypeList failed", error);
      cachedPolicyErrorTypeListPromise = null;
      return [];
    });
  return cachedPolicyErrorTypeListPromise;
};

// PascalCase wire DTO from /api/lookup/GetPIList/DateType.
export type DateTypeDto = {
  DateTypeCode: string;
  DateTypeName: string;
};

// Cache keyed by subType (mirrors getCountyList's Map approach) so callers for
// different subTypes (PolicyTransaction vs PolicyHistory) cache independently.
const cachedDateTypeListByKey = new Map<string, Promise<DateTypeDto[]>>();

export const getDateTypeList = (
  subType: string = "PolicyTransaction"
): Promise<DateTypeDto[]> => {
  const cached = cachedDateTypeListByKey.get(subType);
  if (cached) return cached;
  const promise = axiosSecuredInstance
    .get<DateTypeDto[]>("/lookup/GetPIList/DateType", {
      params: { subType },
    })
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error("getDateTypeList failed", error);
      cachedDateTypeListByKey.delete(subType);
      return [];
    });
  cachedDateTypeListByKey.set(subType, promise);
  return promise;
};

// PascalCase wire DTO from /api/lookup/GetPIList/TransactionType. 
export type TransactionTypeDto = {
  TransactionTypeID: number;
  TransactionTypeCode: string;
  TransactionTypeName: string;
  TransactionTypeDescription: string;
  SubType: string | null;
  SortOrder: number | null;
  Active: number;
};

let cachedTransactionTypeListPromise: Promise<TransactionTypeDto[]> | null = null;

export const getTransactionTypeList = (): Promise<TransactionTypeDto[]> => {
  if (cachedTransactionTypeListPromise) return cachedTransactionTypeListPromise;
  cachedTransactionTypeListPromise = axiosSecuredInstance
    .get<TransactionTypeDto[]>("/lookup/GetPIList/TransactionType")
    .then(({ data }) =>
      (Array.isArray(data) ? data : [])
        .filter((t) => t.Active === 1)
        .sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0))
    )
    .catch((error) => {
      console.error("getTransactionTypeList failed", error);
      cachedTransactionTypeListPromise = null;
      return [];
    });
  return cachedTransactionTypeListPromise;
};

// Static list of all transaction codes from /api/lookup/GetTransCodesAll.
// Response is a flat string[] — used as both label and value in dropdowns.
let cachedTransCodesAllPromise: Promise<string[]> | null = null;

export const getTransCodesAll = (): Promise<string[]> => {
  if (cachedTransCodesAllPromise) return cachedTransCodesAllPromise;
  cachedTransCodesAllPromise = axiosSecuredInstance
    .get<string[]>("/lookup/GetTransCodesAll")
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error("getTransCodesAll failed", error);
      cachedTransCodesAllPromise = null;
      return [];
    });
  return cachedTransCodesAllPromise;
};

// Typeahead helpers for the Policy Management column-header dropdowns
// (Policy Form Name / File Name / Original Policy Number). Each backend
// returns IEnumerable<string> — a plain string[] on the wire.
const searchByText = async (
  url: string,
  text: string,
  listCount?: number
): Promise<string[]> => {
  if (!text || text.trim().length < 1) return [];
  try {
    const { data } = await axiosSecuredInstance.get<string[]>(url, {
      params: { text, listCount },
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(`${url} failed`, error);
    return [];
  }
};

export const getFormNameListByText = (text: string) =>
  searchByText(
    "/PolicyTransaction/GetFormNameListByText",
    text,
    50
  );

export const getFileNumberListByText = (text: string) =>
  searchByText(
    "/PolicyTransaction/GetFileNumberListByText",
    text,
    50
  );

export const getOriginalPolicyNumberListByText = (text: string) =>
  searchByText(
    "/PolicyTransaction/GetOriginalPolicyNumberListByText",
    text,
    50
  );

// History-tab variants of the typeahead endpoints — same min-1-char gate +
// 50-result cap, but hit the PolicyInformationHistory controller.
export const getPolicyHistoryFormNameListByText = (text: string) =>
  searchByText(
    "/PolicyHistory/GetPolicyHistoryFormNameListByText",
    text,
    50
  );

export const getPolicyHistoryFileNumberListByText = (text: string) =>
  searchByText(
    "/PolicyHistory/GetPolicyHistoryFileNumberListByText",
    text,
    50
  );

export const getPolicyHistoryOriginalPolicyNumberListByText = (text: string) =>
  searchByText(
    "/PolicyHistory/GetPolicyHistoryOriginalPolicyNumberListByText",
    text,
    50
  );

type ClosedByUserOptionDto = {
  ClosedByUserID: string;
  ClosedByUserName: string;
};

export const getPolicyHistoryClosedByListByText = async (
  text: string
): Promise<OptionRenderType[]> => {
  if (!text || text.trim().length < 1) return [];
  try {
    const { data } = await axiosSecuredInstance.get<ClosedByUserOptionDto[]>(
      "/PolicyHistory/GetPolicyHistoryClosedByListByText",
      { params: { text, listCount: 50 } }
    );
    return (Array.isArray(data) ? data : []).map((d) => ({
      label: d.ClosedByUserName,
      value: d.ClosedByUserID,
    }));
  } catch (error) {
    console.error("getPolicyHistoryClosedByListByText failed", error);
    return [];
  }
};

// PascalCase wire DTO from /api/lookup/SearchCompanyLocations.
export type CompanyLocationDto = {
  CompanyID: string;
  CompanyName: string;
  CompanyLegacyID: string;
  LocationUniqueID: string;
  LocationLegacyID: string;
  LocationDisplayName: string;
  LocationFullDisplayName: string;
  LocationStateCode: string;
  IsActive: boolean;
};

// Async typeahead for the Agent ID column-header dropdown in Quality Review.
// Min-length-2 guard mirrors AutocompleteInput's debounced-typeahead pattern.
export const searchCompanyLocations = async (
  searchText: string
): Promise<CompanyLocationDto[]> => {
  if (!searchText || searchText.trim().length < 2) return [];
  try {
    const { data } = await axiosSecuredInstance.get<CompanyLocationDto[]>(
      `/lookup/SearchCompanyLocations?searchText=${encodeURIComponent(searchText)}&showAll=true`
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("searchCompanyLocations failed", error);
    return [];
  }
};
